import React from 'react';
import PropTypes from 'prop-types';
import { FLAGS_NONREMOVABLE } from 'utils/constants';
import {
  ERROR_TEXT_NO_DEFAULT_VALUE,
  ERROR_TEXT_NO_DROPDOWN_OPTION_LABEL,
  ERROR_TEXT_NO_SELECTED_DROPDOWN_OPTIONS,
} from 'langs/en/errors';
import SelectAttribute from 'components/SelectAttribute';
import TypesContainer from 'components/Fields/TypesContainer';
import InputContainer from 'components/InputContainer';
import { convertDropdownValueToKey } from 'utils/idUtils';
import Table from './Dropdown.Table';

class FieldDropdown extends React.Component {

  validateAndChangeBulkShouldDisplay = (toggleEligibleOptions, value) => {
    this.props.bulkToggleDropdownOptions(this.props.values.id, toggleEligibleOptions, value);
    if (!value && toggleEligibleOptions.length === this.props.values.editors.dropdownConfig.optionsOrder.length) {
      this.props.fieldValidityChanged([...this.props.keyPath, 'dropdownConfig', 'options'], false, ERROR_TEXT_NO_SELECTED_DROPDOWN_OPTIONS);
    } else {
      this.props.fieldValidityChanged([...this.props.keyPath, 'dropdownConfig', 'options'], true);
    }
  }

  validateAndChangeShouldDisplay = (keyPath, value) => {
    this.props.onFieldValueChange(keyPath, value);
    this.props.onFieldUIStateChange(keyPath, { touched: true });
    const { editors: { dropdownConfig } } = this.props.values;
    const optionId = keyPath[keyPath.length - 2];
    const { defaultOption } = dropdownConfig;
    const changedOptionValue = dropdownConfig.options[optionId].value;

    if (defaultOption === changedOptionValue) {
      this.props.onFieldValueChange([...this.props.keyPath, 'dropdownConfig', 'defaultOption'], '');
    }

    const optionsToDisplay = dropdownConfig.optionsOrder.filter((optId) => {
      if (optionId === optId) return value;
      return dropdownConfig.options[optId].shouldDisplay;
    });

    if (optionsToDisplay.length < 1) {
      this.props.fieldValidityChanged([...this.props.keyPath, 'dropdownConfig', 'options'], false, ERROR_TEXT_NO_SELECTED_DROPDOWN_OPTIONS);
    } else {
      this.props.fieldValidityChanged([...this.props.keyPath, 'dropdownConfig', 'options'], true);
    }

    if (dropdownConfig.options[optionId].label === '') {
      if (value) {
        this.props.fieldValidityChanged([...this.props.keyPath, 'dropdownConfig', 'options', optionId, 'label'], false, ERROR_TEXT_NO_DROPDOWN_OPTION_LABEL);
      } else {
        this.props.fieldValidityChanged([...this.props.keyPath, 'dropdownConfig', 'options', optionId, 'label'], true);
      }
    }
  };

  validateAndChangeDefaultOption = (keyPath, value) => {
    this.props.onFieldValueChange(keyPath, value);
    if (this.props.values.hidden && value.length === 0) {
      this.props.fieldValidityChanged(keyPath, false, ERROR_TEXT_NO_DEFAULT_VALUE);
    } else {
      this.props.fieldValidityChanged(keyPath, true);
    }
  }

  validateAndChangeOptionLabel = (keyPath, value) => {
    this.props.onFieldValueChange(keyPath, value);

    if (value === '') {
      this.props.fieldValidityChanged(keyPath, false, ERROR_TEXT_NO_DROPDOWN_OPTION_LABEL);
    } else {
      this.props.fieldValidityChanged(keyPath, true);
    }
  }

  createSelectOptionsObject = (optionsObject) => {
    const optionsKeys = Object.keys(optionsObject);
    const selectOptions = {};
    optionsKeys.forEach((key) => {
      const current = optionsObject[key];
      if (current.shouldDisplay) {
        selectOptions[[current.value]] = current.label;
      }
    });
    return selectOptions;
  }

  render() {
    const { editors: { dropdownConfig } } = this.props.values;
    const { graph } = this.props.fieldConditionalityInfo;
    let toggleableOptionIds = [...dropdownConfig.optionsOrder];
    const userBranchingVerticies = graph.getVerticiesForUserBranchingField(this.props.values.id);
    if (userBranchingVerticies) {
      const orphanVerticies = graph.getOrphanedUserBranches();
      toggleableOptionIds = orphanVerticies
        .filter((vertex) => userBranchingVerticies[vertex])
        .map((vertex) => userBranchingVerticies[vertex])
        .map(convertDropdownValueToKey);
    }

    const selectOptionsMap = this.createSelectOptionsObject(dropdownConfig.options);
    const standardFieldNameIsNull = !this.props.values.name;
    const requiredStandardField = this.props.values.flags && this.props.values.flags.includes(FLAGS_NONREMOVABLE);
    return (
      <div className="col mt-2">
        <div className="row mb-4">
          <div className="col-sm-6">
            <TypesContainer
              disabled={requiredStandardField || standardFieldNameIsNull}
              radioGroupName="Field Type"
              keyPath={this.props.keyPath}
              onFieldValueChange={this.props.onFieldValueChange}
              values={this.props.values}
              uiState={this.props.uiState.type}
              isDropdownOrRadio
            />
          </div>
          <div className="col-sm-6">
            <InputContainer
              label="Default Value"
              keyPath={[...this.props.keyPath, 'dropdownConfig', 'defaultOption']}
              uiState={this.props.uiState.editors.dropdownConfig.defaultOption}
              disabled={standardFieldNameIsNull || requiredStandardField}
            >
              <SelectAttribute
                keyPath={[...this.props.keyPath, 'dropdownConfig', 'defaultOption']}
                value={dropdownConfig.defaultOption}
                autofocus={standardFieldNameIsNull}
                options={selectOptionsMap}
                onFieldValueChange={this.validateAndChangeDefaultOption}
                onFieldUIStateChange={this.props.onFieldUIStateChange}
              />
            </InputContainer>
          </div>
        </div>
        <div className="row">
          <Table
            id={this.props.values.id}
            keyPath={[...this.props.keyPath, 'dropdownConfig']}
            toggleableOptionIds={toggleableOptionIds}
            onChangeShouldDisplay={this.validateAndChangeShouldDisplay}
            onChangeOptionLabel={this.validateAndChangeOptionLabel}
            onFieldUIStateChange={this.props.onFieldUIStateChange}
            onReorderDropdown={this.props.reorderDropdown}
            onToggleBulkShouldDisplay={(_, value) => this.validateAndChangeBulkShouldDisplay(toggleableOptionIds, value)}
            uiState={this.props.uiState.editors.dropdownConfig}
            values={dropdownConfig}
          />
        </div>
      </div>
    );
  }
}

function FieldDropdownConfigure(props) {
  return <FieldDropdown {...props} />;
}

FieldDropdown.propTypes = {
  bulkToggleDropdownOptions: PropTypes.func.isRequired,
  fieldValidityChanged: PropTypes.func.isRequired,
  fieldConditionalityInfo: PropTypes.object.isRequired,
  values: PropTypes.shape({
    editors: PropTypes.shape({
      dropdownConfig: PropTypes.shape({
        defaultOption: PropTypes.string.isRequired,
        options: PropTypes.object.isRequired,
        optionsOrder: PropTypes.array.isRequired,
      }).isRequired,
    }).isRequired,
    flags: PropTypes.array.isRequired,
    hidden: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    type: PropTypes.string.isRequired,
    uiOrder: PropTypes.number,
    __typename: PropTypes.string,
  }).isRequired,
  uiState: PropTypes.object.isRequired,
  keyPath: PropTypes.array.isRequired,
  onFieldUIStateChange: PropTypes.func.isRequired,
  onFieldValueChange: PropTypes.func.isRequired,
  reorderDropdown: PropTypes.func.isRequired,
};

export default FieldDropdownConfigure;
